<template>
  <dp-container v-if="loading">
    <dp-loading />
  </dp-container>
  <dp-container v-else>
    <section class="mt-3 mx-4">
      <ProductConfigBackLink product-type="WalletCard" />
      <h1 class="text-center">
        {{ $t('walletCardProduct.title') }}
      </h1>
      <!-- eslint-disable-next-line vue/no-v-html (Make sure it's always fetched from API) -->
      <h2 v-if="productStyle" class="text-center text-dark h3 mx-3 px-lg-5" v-html="productStyle.description" />
    </section>
    <section class="mt-3">
      <b-row class="mt-lg-5 mx-4">
        <!-- Image Preview -->
        <b-col class="px-0" lg="5" sm="12">
          <b-row align-h="center" class="text-center flex-column sticky">
            <b-col class="mb-4">
              <h2 class="mb-0 pb-1">{{ $t('walletCardProduct.productImageLabel.front') }}</h2>
              <b-img :src="frontImage" center fluid :alt="`${frontImageAlt ? frontImageAlt : $t('walletCardProduct.productImageAltText.front')}`" />
            </b-col>
            <b-col class="mb-4">
              <h2 class="mb-0 pb-1 mt-md-3">{{ $t('walletCardProduct.productImageLabel.back') }}</h2>
              <b-img :src="backImage" center fluid :alt="`${backImageAlt ? backImageAlt : $t('walletCardProduct.productImageAltText.back')}`" />
            </b-col>
          </b-row>
        </b-col>
        <!-- Wallet Card Product Pre-Customize Config -->
        <b-col class="px-0" lg="6" offset-lg="1" sm="12">
          <section>
            <h2 class="mb-2">{{ $t('walletCardProduct.petSelect.title') }}</h2>
            <DpSelect v-model="selectedPetId" :options="options" class="w-100" rules="required" />
            <b-button :disabled="isButtonDisabled" block class="mt-4" variant="primary" @click="redirectWalletCardStyle">
              {{ $t(`walletCardProduct.buttons.next.${selectedPetName ? 'wPet' : 'default'}`, [selectedPetName]) }}
            </b-button>
          </section>
        </b-col>
      </b-row>
    </section>
  </dp-container>
</template>

<script>
import { mapState } from 'vuex';
import { DpContainer, DpLoading } from '@docupet/component-library';
import DpSelect from '@/components/library/Select';
import getImage from '@/mixins/getImage';
import ProductConfigBackLink from '@/components/common/ProductConfigBackLink';
import { NotificationMixins } from '@/mixins/notification';
import { LanguageCheckMixins } from '@/mixins/languageCheck';

export default {
  name: 'WalletCardProduct',
  components: { ProductConfigBackLink, DpContainer, DpLoading, DpSelect },
  mixins: [getImage, NotificationMixins, LanguageCheckMixins],
  props: {
    styleId: {
      type: [String, Number],
      default: '',
      required: false,
    },
    defaultPetId: {
      type: String,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      selectedPetId: this.defaultPetId,
    };
  },
  metaTag() {
    return {
      ...this.$router.currentRoute.meta,
      title: `${this.selectedPetName ? `${this.selectedPetName}'s` : ''} `,
    };
  },
  computed: {
    ...mapState({
      loading: (state) => state.user.loading || state.order.loading,
      order: (state) => state.order.order,
      orderId: (state) => state.orderId,
      city: (state) => state.user.city,
      cityId: (state) => state.cityId,
      user: (state) => state.user.user,
      productGroups: (state) => state.walletCardProduct.groups,
      productStyle: (state) => state.walletCardProduct.style,
      petOwner: (state) => state.order?.order?.petOwner,
      pets: (state) => state.order?.order?.petOwner?.pets ?? [],
    }),
    productImages() {
      if (!this.productStyle?.image || !this.productStyle?.images) {
        return [];
      }
      return [this.productStyle.image, ...this.productStyle.images]?.filter((image) => !!image && !!image?.original);
    },
    frontImage() {
      return this.getImage(this.productImages?.[0]?.original);
    },
    backImage() {
      return this.getImage(this.productImages?.[1]?.original);
    },
    frontImageAlt() {
      return this.productImages?.[0]?.imageAltText;
    },
    backImageAlt() {
      return this.productImages?.[1]?.imageAltText;
    },
    options() {
      return [
        { value: null, text: this.$i18n.t('walletCardProduct.petSelect.options.default'), disabled: true },
        ...this.pets?.map(({ id, name }) => ({
          value: id,
          text: `${name}`,
        })),
        {
          value: null,
          text: this.$i18n.t('walletCardProduct.petSelect.options.addNew'),
          to: {
            name: 'AddPet',
            params: {
              redirect_path: 'WalletCardProduct',
              redirect_params: { styleId: this.styleId },
              redirect_pet_id_param: 'defaultPetId',
            },
          },
        },
      ];
    },
    selectedPet() {
      return this.pets?.find(({ id }) => id === this.selectedPetId);
    },
    selectedPetName() {
      return this.selectedPet?.name;
    },
    isButtonDisabled() {
      return !this.selectedPetId || this.languageError;
    },
  },
  created() {
    (() => {
      if (this.productGroups?.length > 0) {
        return Promise.resolve(this.productGroups);
      }
      return this.$store.dispatch('walletCardProduct/fetchGroups', { cityId: this.city?.id || this.cityId });
    })().then(() => {
      this.$store.dispatch('walletCardProduct/setStyleById', {
        styleId: this.styleId,
      });
    });

    if (this.pets?.length === 1) {
      this.selectedPetId = this.pets?.[0]?.id;
    }
  },
  methods: {
    redirectWalletCardStyle() {
      this.$router.push({
        name: 'WalletCardStyle',
        params: {
          petId: this.selectedPetId,
          styleId: this.styleId,
        },
        query: {
          redirect_path: 'wallet-card-store',
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.sticky {
  position: sticky;
  position: -webkit-sticky;
  top: 0;
  margin-top: 0;
  padding-top: 0;
  z-index: 999;
}
</style>
